import { useEffect, useRef, useState } from 'react';
import { KMSWidgetConfig } from '../../widget/types';

interface WidgetPreviewMiniProps {
  config: Partial<KMSWidgetConfig>;
  autoOpen?: boolean;
}

const WidgetPreviewMini = ({ config, autoOpen = false }: WidgetPreviewMiniProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [previewKey, setPreviewKey] = useState(0);

  // Get API origin
  const isDev = typeof window !== 'undefined' && window.location.hostname === 'localhost';
  const apiOrigin = isDev ? 'http://localhost:8100' : window.location.origin;

  // Helper functions for widget configuration
  const getButtonBorderRadius = () => {
    const radius = config.buttonBorderRadius || '50%';
    if (radius === '50%') return '50%';
    if (radius.includes('px')) return radius;
    return '50%';
  };

  const getPopupBorderRadius = () => {
    const radiusMap: Record<string, string> = {
      '0': '0',
      '8px': '8px',
      '12px': '12px',
      '16px': '16px',
      '24px': '24px',
    };
    return radiusMap[config.popupBorderRadius || '12px'] || '12px';
  };

  const getWidgetHeight = () => {
    const heightMap: Record<string, string> = {
      '400px': '400px',
      '550px': '550px',
      '650px': '650px',
      '90vh': '90vh',
    };
    return heightMap[config.widgetHeight || '550px'] || '550px';
  };

  // Generate preview HTML
  const generatePreviewHtml = () => {
    const suggestedQuestionsJson = JSON.stringify(config.suggestedQuestions || []);

    return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Widget Preview</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
      min-height: 100vh;
      position: relative;
      overflow: hidden;
    }
  </style>
</head>
<body>
  <!-- KMS Chat Widget -->
  <script type="text/javascript">
    window.$kmsWidget = [];
    window.KMS_WIDGET_CONFIG = {
      botId: 'preview',
      apiOrigin: '${apiOrigin}',
      widgetOrigin: '${apiOrigin}',
      position: '${config.position || 'bottom-right'}',
      themeColor: '${config.themeColor || '#3B82F6'}',
      accentColor: '${config.accentColor || '#10B981'}',
      textColor: '${config.textColor || '#1F2937'}',
      buttonColor: '${config.buttonColor || '#3B82F6'}',
      buttonIconColor: '${config.buttonIconColor || '#FFFFFF'}',
      buttonIcon: '${config.buttonIcon || 'message-circle'}',
      buttonBorderRadius: '${getButtonBorderRadius()}',
      welcomeMessage: '${(config.welcomeMessage || 'How can I help you today?').replace(/'/g, "\\'")}',
      buttonText: '${(config.buttonText || 'Chat').replace(/'/g, "\\'")}',
      autoOpen: ${autoOpen || config.autoOpen || false},
      autoOpenDelay: ${config.autoOpenDelay || 800},
      preview: true,
      headerTitle: '${(config.headerTitle || 'AI Assistant').replace(/'/g, "\\'")}',
      botName: '${(config.botName || 'Assistant').replace(/'/g, "\\'")}',
      avatarIcon: '${config.avatarIcon || '🤖'}',
      showPoweredBy: ${config.showPoweredBy !== false},
      widgetSize: '${config.widgetSize || 'normal'}',
      widgetHeight: '${getWidgetHeight()}',
      popupBorderRadius: '${getPopupBorderRadius()}',
      inputPlaceholder: '${(config.inputPlaceholder || 'Type your question...').replace(/'/g, "\\'")}',
      sendButtonText: '${(config.sendButtonText || 'Send').replace(/'/g, "\\'")}',
      suggestedQuestions: ${suggestedQuestionsJson}
    };
    (function() {
      var d = document;
      var s = d.createElement("script");
      s.src = "${apiOrigin}/widget.js?t=" + Date.now();
      s.async = 1;
      s.onerror = function() {
        console.error('Failed to load widget script');
      };
      d.getElementsByTagName("head")[0].appendChild(s);
    })();
  </script>
</body>
</html>`;
  };

  // Reload preview when config changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setPreviewKey((prev) => prev + 1);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [
    config.themeColor,
    config.accentColor,
    config.textColor,
    config.buttonColor,
    config.buttonIconColor,
    config.buttonIcon,
    config.buttonBorderRadius,
    config.position,
    config.headerTitle,
    config.botName,
    config.avatarIcon,
    config.welcomeMessage,
    config.autoOpen,
    autoOpen,
  ]);

  return (
    <div className="w-full h-full bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl overflow-hidden flex items-center justify-center">
      <div className="relative w-full h-full max-w-2xl max-h-[600px] rounded-2xl">
        <iframe
          ref={iframeRef}
          key={previewKey}
          srcDoc={generatePreviewHtml()}
          className="w-full h-full border-0 rounded-2xl"
          title="Widget Preview"
          sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
        />
      </div>
    </div>
  );
};

export default WidgetPreviewMini;
